import React from "react";
import {Organization} from "./sso.js";
import {ForeignKey, Model, isEmpty, formatDate} from "weed-js";
import {DateTime} from "luxon";
import {
    CanonicalCustomer,
    CanonicalIntegrationAccount,
    CanonicalBrand,
    CanonicalCategory,
    CanonicalEmployee,
    CanonicalInventory,
    CanonicalMasterCategory,
    CanonicalPackage,
    CanonicalProducer,
    CanonicalProduct,
    CanonicalStrain,
    CanonicalTransaction,
    CanonicalTransactionItem,
    CanonicalVendor, CanonicalFacility
} from "./canonical";
import {DUTCHIE} from "../constants";


export class DutchieModel extends Model {
    static namespace = DUTCHIE;
    static integration = DUTCHIE;

    static dal(client) {
        return client.dutchie.dal.v1();
    }

}

export class DutchieAccount extends DutchieModel {
    static columns = ["id", "display_name", "organization_id"];
    static pluralName = "dutchie_accounts";
    static uri = "/leaflogix-accounts/";

    static canonicalClasses = [CanonicalIntegrationAccount, CanonicalFacility];

    static dal = (client) => client.sso.dal.v1();

    static foreignKeys = {
        organization: new ForeignKey(Organization)
    }
}

export class Customer extends DutchieModel {
    static columns = ["id", "dutchie_account_id", "date_of_birth",
        "gender", "type_name"]
    static uri = "/customers/";
    static dateField = "transactions__transaction_date";
    static productIdField = "transactions__items__product_id";
    static pluralName = "customers";
    static canonicalClasses = [CanonicalCustomer];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            transactions__dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    static foreignKeys = {
        dutchie_account: new ForeignKey(DutchieAccount)
    };
}

export class Employee extends DutchieModel {
    static columns = ["id", "dutchie_account_id", "external_id"];
    static uri = "/employees/";
    static dateField = "transactions__transaction_date";
    static pluralName = "employees";
    static canonicalClasses = [CanonicalEmployee];
    static productIdField = "transactions__items__product_id";

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            transactions__dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    static foreignKeys = {
        dutchie_account: new ForeignKey(DutchieAccount)
    };
}

export class Transaction extends DutchieModel {
    static columns = [
        "id", "dutchie_account_id", "transaction_date", "loyalty_earned",
        "loyalty_spent", "subtotal", "tax", "total", "total_discount",
        "total_items", "customer_id", "employee_id", "order_method",
        "order_source", "order_type", "transaction_type", "external_id"
    ];
    static uri = "/transactions/";
    static dateField = "transaction_date";
    static productIdField = "items__product__id";
    static pluralName = "transactions";
    static canonicalClasses = [CanonicalTransaction];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    static foreignKeys = {
        customer: new ForeignKey(Customer),
        employee: new ForeignKey(Employee),
    }
}

export class MasterCategory extends DutchieModel {
    static uri = "/master-categories/";
    static columns = [
        "id", "name", "dutchie_account_id"
    ];
    static dateField = "categories__products__items__transaction__transaction_date";
    static productIdField = "categories__products__id";
    static pluralName = "master_categories";
    static canonicalClasses = [CanonicalMasterCategory];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            categories__products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}

export class Strain extends DutchieModel {
    static uri = "/strains/";
    static columns = ["id", "name", "dutchie_account_id"];
    static dateField = "products__items__transaction__transaction_date";
    static pluralName = "strains";
    static canonicalClasses = [CanonicalStrain];
    static productIdField = "products__id";

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}

export class Vendor extends DutchieModel {
    static uri = "/vendors/";
    static columns = ["id", "name", "dutchie_account_id"];
    static dateField = "products__items__transaction__transaction_date";
    static pluralName = "vendors";
    static canonicalClasses = [CanonicalVendor];
    static productIdField = "products__id";

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}


export class Producer extends DutchieModel {
    static uri = "/producers/";
    static columns = ["id", "name", "dutchie_account_id"];
    static dateField = "products__items__transaction__transaction_date";
    static pluralName = "producers";
    static canonicalClasses = [CanonicalProducer];
    static productIdField = "products__id";

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}


export class Category extends DutchieModel {

    static uri = "/categories/";
    static columns = ["id", "dutchie_account_id", "name", "master_category_id"];
    static dateField = "products__items__transaction__transaction_date";
    static productIdField = "products__id";
    static pluralName = "categories";
    static foreignKeys = {
        master_category: new ForeignKey(MasterCategory)
    };
    static canonicalClasses = [CanonicalCategory];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}

export class Brand extends DutchieModel {

    static uri = "/brands/";
    static columns = ["id", "name", "dutchie_account_id"];
    static dateField = "products__items__transaction__transaction_date";
    static productIdField = "products__id";
    static pluralName = "brands";
    static canonicalClasses = [CanonicalBrand];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            products__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}

export class Product extends DutchieModel {
    static uri = "/products/";
    // Note; if you change the columns here you need to update them in the ProductCatalogAPIView dutchie view as well.
    static columns = [
        "id", "dutchie_account_id", "category_id", "brand_id",
        "name", "price", "strain_id", "vendor_id", "unit_cost",
        "producer_id", "sku", "image_url"
    ];
    static dateField = "items__transaction__transaction_date";
    static pluralName = "products";
    static productIdField = "id";

    static foreignKeys = {
        category: new ForeignKey(Category),
        strain: new ForeignKey(Strain),
        brand: new ForeignKey(Brand),
        vendor: new ForeignKey(Vendor),
        producer: new ForeignKey(Producer)
    };

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    static canonicalClasses = [CanonicalProduct];

}

export class Package extends DutchieModel {
    static uri = "/packages/";
    static columns = ["id", "external_id", "dutchie_account_id", "label", "product_id"];
    static dateField = "product__items__transaction__transaction_date";
    static productIdField = "product__id";
    static pluralName = "packages";

    static foreignKeys = {
        product: new ForeignKey(Product),
    }
    static canonicalClasses = [CanonicalPackage];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            product__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }
}

export class Inventory extends DutchieModel {

    static uri = "/inventories/";
    static columns = [
        "id", "description", "expiration_datetime", "image_url",
        "quantity_available", "unit_cost", "unit_price", "batch_id",
        "brand_id", "dutchie_account_id", "package_id", "product_id",
        "deleted"
    ];
    static dateField = "product__items__transaction__transaction_date";
    static productIdField = "product__id";
    static pluralName = "inventories";

    static foreignKeys = {
        product: new ForeignKey(Product),
        package: new ForeignKey(Package),
        brand: new ForeignKey(Brand),
    };
    static canonicalClasses = [CanonicalInventory];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            product__items__transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    static fetchByDate({client, startDate, endDate, dateFormatter, abortController, ...overrides}) {
        console.log(`Fetching Inventories (fast) from Dutchie between ${startDate} and ${endDate}`);
        return client.dutchie.api.v1().get("/fast-inventory/", {
            params: {
                dutchie_account_id__in: overrides?.dutchie_account_id__in,
                proj: Inventory.columns.join(","),
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
            },
        }).then(response => {
            console.log("Got response from fast inventory: ", response);

            return (response?.data?.results || []).map(data => {
                return Inventory.byId.get(data.id) || new Inventory(data);
            });
        }).catch(err => {
            console.error("Error fetching Inventories from Dutchie fast-inventory endpoint", err);

        });
    }
}

export class Item extends DutchieModel {
    static uri = "/items/";
    static columns = [
        "id", "dutchie_account_id", "batch_id", "package_id", "product_id",
        "transaction_id", "vendor_id", "quantity", "total_discount",
        "total_price", "unit_cost", "unit_price", "external_id"
    ];
    static dateField = "transaction__transaction_date";
    static productIdField = "product_id";
    static pluralName = "items";
    static foreignKeys = {
        product: new ForeignKey(Product),
        transaction: new ForeignKey(Transaction),
        dutchie_account: new ForeignKey(DutchieAccount),
        vendor: new ForeignKey(Vendor),
    };
    static canonicalClasses = [CanonicalTransactionItem];

    static getParams({startDate, endDate, dutchie_account_id__in}) {
        return {
            transaction__dutchie_account_id__in: dutchie_account_id__in,
        }
    }

    toNumber(value) {
        if (value === null || value === undefined) {
            return value;
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
            throw new Error(`Value ${value} is not a valid number for Item with id ${this.id}`);
        }
        return num;
    }

    /*
    toRow() {
        if (isEmpty(this.product)) {
            throw new Error("Product with id " + this.product_id + " was not found for Item with id " + this.id);
        }
        if (isEmpty(this.transaction)) {
            throw new Error("Transaction with id " + this.transaction_id + " was not found for Item with id " + this.id);
        }
        if (isEmpty(this.dutchie_account)) {
            throw new Error("DutchieAccount with id " + this.dutchie_account_id + " was not found for Item with id " + this.id);
        }

        const txnDate = DateTime.fromISO(this.transaction.transaction_date, {zone: "America/New_York"}).toLocaleString();
        const inventory = isEmpty(this.product.inventories) ? {} : Array.from(this.product.inventories).find(inv => inv.package_id === this.package_id);
        const isDeleted = isEmpty(inventory) ? true : inventory.deleted;
        const organizationId = this.dutchie_account.organization_id;
        const inventoryUnitCost = isDeleted ? 0 : inventory.unit_cost;
        const inventoryUnitPrice = isDeleted ? 0 : inventory.unit_price;
        const unitCost = this.toNumber(isEmpty(this.unit_cost) ? inventoryUnitCost : this.unit_cost);
        const unitPrice = this.toNumber(this.unit_price === null ? inventoryUnitPrice : this.unit_price);
        const quantityAvailable = this.toNumber(isDeleted ? 0 : inventory.quantity_available);
        const employeeId = this.transaction?.employee_id;
        const employeeExternalId = isEmpty(this.transaction?.employee) ? null : this.transaction.employee.external_id;
        const quantitySold = this.toNumber(this.quantity);
        if (employeeId && isEmpty(employeeExternalId)) console.warn(`Employee with id ${employeeId} does not have an external_id set. This may cause issues with reporting.`);


        console.log("Inventory", inventory, "quantity available", quantityAvailable, "unit cost", unitCost, "unit price", unitPrice, "isDeleted", isDeleted);


        return [
            this.transaction.external_id,
            this.external_id,
            this.transaction?.employee?.external_id ? this.transaction.employee.external_id : "---",
            isEmpty(this.dutchie_account) ? "" : this.dutchie_account.display_name,
            isEmpty(this.product.category) || isEmpty(this.product.category.master_category) ? "---" : this.product.category.master_category.name,
            isEmpty(this.product.category) ? "---" : this.product.category.name,
            isEmpty(this.product.brand) ? "---" : this.product.brand.name,
            isEmpty(this.product.vendor) ? "---" : this.product.vendor.name,
            this.product_id > -1 ? {
                props: {
                    href: `/analytics/organizations/${organizationId}/integration/dutchie/product/${this.product_id}`,
                    role: "link"
                },
                displayValue: this.product.name,
            } : null,
            this.product.sku,
            isDeleted ? 0 : quantityAvailable,
            quantitySold,
            unitPrice * quantitySold,
            unitCost === 0 ? null : unitCost * quantitySold,
            unitCost === 0 || unitPrice === 0 ? null : (unitPrice - unitCost) * quantitySold,
            unitCost,
            unitPrice,
            txnDate
        ];
    }

     */
}
